'use strict';

import NavigationListeners from './common-navigation-listeners';
import Mustache from 'mustache';

let Config = global.__NavigationConfig;
let State = global.__NavigationState;

/**
 * Update states.
 */
function updateGlobals() {
	Config = global.__NavigationConfig;
	State = global.__NavigationState;
}

/* Helpers start */

/**
 * Restores previously selected navigation (i.e. correct target group navigation).
 *
 * @param {Object} [state] - State object. If not given, will use readState function get saved state.
 * @returns {Object} Returns passed state or saved state.
 */
function restoreNavigationState(state) {
	const savedState = state || readNavigationState();
	if (savedState && savedState.navigation) {
		if (
			State.mobileNavigationData.find(
				(item) => item.navId === savedState.navigation
			)
		) {
			selectNavigation(savedState.navigation);
		}
	}

	return savedState;
}

/**
 * Reads saved navigation state.
 *
 * @returns {(Object|boolean)} Navigation state or false if couldn't read saved state.
 */
function readNavigationState() {
	let storageType = localStorage;
	if (!storageType) {
		storageType = sessionStorage;
	}

	if (storageType && storageType.getItem(Config.storageKey)) {
		try {
			return JSON.parse(storageType.getItem(Config.storageKey));
		} catch (error) {
			return false;
		}
	} else {
		return false;
	}
}

/**
 * Save navigation state.
 *
 * @param {Object} [stateOverrides] - Copies object key & value pairs to current State.
 * @returns {boolean} True if saving was successful, false if not.
 */
function saveNavigationState(stateOverrides) {
	let storageType = localStorage;
	if (!storageType) {
		storageType = sessionStorage;
	}

	if (storageType) {
		try {
			if (stateOverrides && typeof stateOverrides === 'object') {
				for (const key of Object.keys(stateOverrides)) {
					State[key] = stateOverrides[key];
				}
			}
			storageType.setItem(
				Config.storageKey,
				JSON.stringify({ navigation: State.navigation })
			);
		} catch (error) {
			return false;
		}
		return true;
	} else {
		return false;
	}
}

/**
 * Delete navigation state
 * @returns {boolean} True if deleting was successful, false if not.
 */
function deleteNavigationState() {
	let storageType = localStorage;
	if (!storageType) {
		storageType = sessionStorage;
	}

	if (storageType) {
		try {
			storageType.removeItem(Config.storageKey);
		} catch (error) {
			return false;
		}
		return true;
	} else {
		return false;
	}
}

/**
 * Get element transition duration in milliseconds
 *
 * @param {Element} element - Element used.
 * @param {string} transitionProperty - Transition property i.e. "opacity".
 * @param {string} [defaultTiming=0.3s] - Fallback default timing duration.
 * @returns
 */
function getTransitionDuration(
	element,
	transitionProperty,
	defaultTiming = '0.3s'
) {
	const prop = element
		? String(
			String(getComputedStyle(element).transition)
				.trim()
				.split(',')
				.find((prop) => prop.includes(transitionProperty))
		  ).trim()
		: false;

	let timing = element
		? (prop || `${transitionProperty} ${defaultTiming}`).split(' ')[1]
		: defaultTiming;

	let timingMs = 0;

	if (!timing && timing !== '0') {
		timing = defaultTiming;
	}

	if (timing.includes('ms')) {
		timingMs = parseFloat(timing);
	} else {
		timingMs = parseFloat(timing) * 1000;
	}

	return timingMs;
}

/**
 * Debounce throttle.
 *
 * @param {function} func - Throttled/debounced function.
 * @param {number} limitMs - Delay in milliseconds.
 * @param {string} uniqueId - Id used for throttling/debouncing.
 */
function debounceThrottle(func, limitMs, uniqueId) {
	if (typeof State.timers[uniqueId] === 'undefined') {
		State.timers[uniqueId] = {};
	}

	if (!State.timers[uniqueId].lastRan) {
		func.apply();
		State.timers[uniqueId].lastRan = Date.now();
		return func;
	} else {
		clearTimeout(State.timers[uniqueId].last);
		State.timers[uniqueId].last = setTimeout(function () {
			if (
				State.timers[uniqueId] &&
				Date.now() - State.timers[uniqueId].lastRan >= limitMs
			) {
				func.apply();
				State.timers[uniqueId].lastRan = Date.now();
			}
		}, limitMs - (Date.now() - State.timers[uniqueId].lastRan));
	}
}

/* Helpers end */

/**
 * Selects navigation.
 *
 * @param {string} navigationId - Navigation to be selected (id).
 * @param {boolean} [skipRendering] - Skips rendering mobile navigation.
 * @param {function} [callback] - Runs callback after selecting navigation.
 * @param {boolean} [skipUnhide] - Skips unhiding navigation selector (target groups selection).
 */
function selectNavigation(navigationId, skipRendering, callback, skipUnhide) {
	const mobileNavigationContainer = document.querySelector(
		'.o-navigation__mobile .o-navigation__mobile-navigation'
	);

	for (const targetGroupLink of document.querySelectorAll(
		'.js-select-navigation-container .a-navigation-toggle'
	)) {
		if (targetGroupLink.dataset.navigation === navigationId) {
			targetGroupLink.title =
				'Valittu kohderyhmä: ' + targetGroupLink.textContent;
			targetGroupLink.classList.add('a-navigation-toggle--selected');
			targetGroupLink.setAttribute('aria-current', true);
			State.navigation = navigationId;
			if (mobileNavigationContainer) {
				mobileNavigationContainer.style.transform = '';
			}

			State.level = 0;

			if (!skipRendering) {
				renderMobileNavigation();
			}
		} else {
			targetGroupLink.classList.remove('a-navigation-toggle--selected');
			targetGroupLink.removeAttribute('aria-current');
			targetGroupLink.title =
				'Valitse kohderyhmä: ' + targetGroupLink.textContent;
		}
	}

	if (!skipUnhide) {
		for (const navigationSelector of document.querySelectorAll(
			'.js-select-navigation-container'
		)) {
			navigationSelector.classList.remove(
				'm-target-group-selector-mobile--hidden'
			);
		}
	}

	for (const desktopNavigation of document.querySelectorAll(
		'.js-navigation-bar'
	)) {
		if (desktopNavigation.dataset.navigationId === navigationId) {
			desktopNavigation.classList.add('m-navigations__bar--selected');
			desktopNavigation.setAttribute('aria-hidden', false);
		} else {
			desktopNavigation.classList.remove('m-navigations__bar--selected');
			desktopNavigation.setAttribute('aria-hidden', true);
		}
	}

	saveNavigationState();

	if (callback) {
		callback();
	}

	// Changes default front page links to match selected target group.
	/*const url = State.navigationLinksById[navigationId];
	if (url) {
		for (const mainPageLink of document.querySelectorAll('[data-main-page-link="true"], a[href="/fi/"], a[href="/"], a[href^="/?"], a[href^="/#"], a[href^="/fi/?"], a[href^="/fi/#"]')) {
			let hrefAttribute = mainPageLink.getAttribute('href');

			if (!mainPageLink.dataset.mainPageLink) {
				mainPageLink.dataset.mainPageLink = true;
			}

			if (!mainPageLink.dataset.originalLink) {
				mainPageLink.dataset.originalLink = hrefAttribute;
			}

			hrefAttribute = mainPageLink.dataset.originalLink;

			if (String(hrefAttribute.includes('?'))) {
				const [, params] = hrefAttribute.split('?');
				if (params) {
					mainPageLink.setAttribute('href', url + '?' + params);
				} else {
					mainPageLink.setAttribute('href', url);
				}
			} else if (String(hrefAttribute.includes('#'))) {
				const [, params] = hrefAttribute.split('#');
				if (params) {
					mainPageLink.setAttribute('href', url + '#' + params);
				} else {
					mainPageLink.setAttribute('href', url);
				}
			} else {
				mainPageLink.setAttribute('href', url);
			}
		}

	}*/
}

/**
 * Close all desktop navigation dropdowns.
 *
 * @param {boolean} [moveFocus=false] - Moves focus back to dropdown button.
 */
function closeAllDropdownNavigations(moveFocus = false) {
	const navDropdownItems = document.querySelectorAll(
		'.a-navigation-item--open'
	);
	let movedFocus = !moveFocus;
	for (const navDropdownItem of navDropdownItems) {
		if (!movedFocus) {
			const navigationBarItem = navDropdownItem.closest(
				'.a-navigation-bar-item'
			);
			if (navigationBarItem) {
				const dropdownItem = navigationBarItem.querySelector(
					'.a-navigation-item--has-dropdown'
				);
				if (dropdownItem) {
					dropdownItem.focus();
				}
			}
			movedFocus = true;
		}
		navDropdownItem.classList.remove('a-navigation-item--open');
		navDropdownItem.setAttribute('aria-expanded', 'false');
	}
}

/**
 * Opens dropdown.
 *
 * @param {Element} dropdownButton - Clicked dropdown button.
 */
function openDropdownNavigation(dropdownButton) {
	if (State.timer) {
		clearTimeout(State.timer);
	}
	if (dropdownButton) {
		closeAllDropdownNavigations();
		dropdownButton.classList.add('a-navigation-item--open');
		dropdownButton.setAttribute('aria-expanded', 'true');
	}
}

/**
 * Toggles dropdown.
 *
 * @param {Element} dropdownButton - Clicked dropdown button.
 */
function toggleDropdown(dropdownButton) {
	if (dropdownButton) {
		if (dropdownButton.classList.contains('a-navigation-item--open')) {
			closeAllDropdownNavigations();
		} else {
			openDropdownNavigation(dropdownButton);
		}
	}
}

/**
 * Renders mobile navigation.
 */
function renderMobileNavigation() {
	const mobileNavigationContainer = document.querySelector(
		'.o-navigation__mobile'
	);
	if (mobileNavigationContainer) {
		const navigationItems = mobileNavigationContainer.querySelector(
			'.o-navigation__mobile-navigation'
		);
		if (navigationItems) {
			const findNavItems = State.mobileNavigationData.find(
				(item) => item.navId === State.navigation
			);

			navigationItems.style.transform = '';
			State.level = 0;
			navigationItems.innerHTML =
				findNavItems && findNavItems.children
					? Mustache.render(State.mobileNavigationItemsTemplate, {
						state: State,
						isMainLevel: State.level === 0,
						isGenericNav: Config.defaultNavigation === State.navigation,
						current: true,
						navigationBlocks: { items: findNavItems.children },
					  })
					: '';

			mobileNavigationContainer.scrollTop = 0;
		}
	}
}

/**
 * Goes back one navigation level. If levels is 0, closes mobile navigation.
 *
 * @param {boolean} [moveFocus] - Moves focus back to previously clicked element when navigating back.
 */
function goBackMobileNavigationLevel(moveFocus) {
	if (State.level === 0) {
		closeMobileNavigation();
		return;
	}
	State.level--;
	const mobileNavigationContainer = document.querySelector(
		'.o-navigation__mobile'
	);
	if (mobileNavigationContainer) {
		const navigationItems = document.querySelector(
			'.o-navigation__mobile .o-navigation__mobile-navigation'
		);
		if (navigationItems) {
			navigationItems.style.transform = `translate3d(calc(-${
				State.level * 100
			}% - ${State.level * 1}rem), 0, 0)`;

			for (const navigationList of navigationItems.querySelectorAll(
				'.o-navigation__mobile-navigation-list-container'
			)) {
				if (
					navigationList.dataset.level &&
					parseInt(navigationList.dataset.level) === State.level
				) {
					navigationList.classList.add(
						'o-navigation__mobile-navigation-list-container--current'
					);
					navigationList.setAttribute('aria-hidden', false);
					navigationList.style.maxHeight = '';
				} else {
					navigationList.classList.remove(
						'o-navigation__mobile-navigation-list-container--current'
					);
					navigationList.setAttribute('aria-hidden', true);
					navigationList.style.maxHeight = `calc(100vh - ${navigationList.parentElement.offsetTop}px)`;
					requestAnimationFrame(() => {
						navigationList.style.maxHeight = '0vh';
					});
				}
			}
			mobileNavigationContainer.scrollTop = 0;
		}

		setTimeout(() => {
			if (moveFocus) {
				focusMobileNavigationListElement(true);
			}
			updateNavigationFocusableElements();
		}, getTransitionDuration(mobileNavigationContainer, 'transform', '0.3s') + 50);
	}
}

/**
 * Renders mobile navigation level.
 *
 * @param {Object[]} menuItemData - Mobile navigation menu blocks. Basically desktop dropdown navigation column but for mobile navigation.
 * @param {Element} clickedMenu - Clicked menu item.
 */
function renderMobileNavigationLevel(menuItemData, clickedMenu) {

	const navigationBlocks = menuItemData.map((navContainer, index) => {

		return {
			// Hide title for first container.
			title: navContainer.containerType === 'sports' ? false : (index === 0 ? false : navContainer.name),
			name: navContainer.name,
			items:
				Array.isArray(navContainer.children) && navContainer.children.length
					? navContainer.children.map(item => {
						item.containerType = navContainer.containerType;
						item.title = (navContainer.containerType !== 'sports' || item.children > 0) ? item.name + navContainer.containerType : false;
						item.hasChildren = navContainer.containerType === 'sports' && item.children;
						return item;
					})
					: navContainer,
			containerType: navContainer.containerType,
			hasChildren: navContainer.containerType === 'sports' && Array.isArray(navContainer.children) && navContainer.children.length > 0,
			navItemId: navContainer.navItemId
		};
	});

	if (navigationBlocks.length === 1) {
		navigationBlocks[0].title = '';
	}

	const mobileNavigationContainer = document.querySelector(
		'.o-navigation__mobile'
	);
	if (mobileNavigationContainer) {
		const navigationItems = mobileNavigationContainer.querySelector(
			'.o-navigation__mobile-navigation'
		);
		if (navigationItems) {
			for (const navigationList of navigationItems.querySelectorAll(
				'.o-navigation__mobile-navigation-list-container'
			)) {
				if (
					navigationList.dataset.level &&
					parseInt(navigationList.dataset.level) > State.level
				) {
					navigationList.remove();
				}
			}

			State.level++;


			for (const navigationList of navigationItems.querySelectorAll(
				'.o-navigation__mobile-navigation-list-container'
			)) {
				navigationList.classList.remove(
					'o-navigation__mobile-navigation-list-container--current'
				);
				navigationList.setAttribute('aria-hidden', true);
				navigationList.style.maxHeight = `calc(100vh - ${navigationList.parentElement.offsetTop}px)`;
				requestAnimationFrame(() => {
					navigationList.style.maxHeight = '0vh';
				});
			}

			const navWrapper = document.createElement('div');

			const backButtonText = clickedMenu
				? String(clickedMenu.textContent).trim()
				: 'Takaisin';

			const sportsContainer = navigationBlocks.map(item => { return item.containerType }).includes('sports');

			const sports = sportsContainer ? {
				name: 'Lajit',
				hasChildren: true,
				items: navigationBlocks.map(item => {
					item.url = typeof item.items !== 'undefined' ? item.items.url : false;
					return item;
				})
			} : false;

			navWrapper.innerHTML = Mustache.render(
				State.mobileNavigationItemsTemplate,
				{
					backButton: { name: backButtonText },
					state: State,
					isMainLevel: State.level === 0,
					isGenericNav: Config.defaultNavigation === State.navigation,
					current: true,
					navigationBlocks: sports || navigationBlocks,
				}
			);

			const newItems = navWrapper.firstChild;
			newItems.setAttribute('aria-hidden', false);
			navigationItems.appendChild(newItems);

			navigationItems.style.transform = `translate3d(calc(-${
				State.level * 100
			}% - ${State.level * 1}rem), 0, 0)`;
			mobileNavigationContainer.scrollTop = 0;
			history.pushState(null, '');
		}
	}
}

/**
 * Updates focusable navigation elements. This is used for mobile menu focus trapping.
 *
 * @param {Element} [mobileNavigation] - Mobile navigation element. If missing, will automatically use .o-navigation__mobile.
 * @returns {boolean} Returns false if .o-navigation__mobile element is missing, otherwise returns true.
 */
function updateNavigationFocusableElements(mobileNavigation) {
	if (!mobileNavigation) {
		mobileNavigation = document.querySelector('.o-navigation__mobile');
	}

	if (!mobileNavigation) {
		return false;
	}

	State.allFocusableElements = Array.from(
		mobileNavigation.querySelectorAll(
			'button, a[href], [tabindex]:not([tabindex="-1"])'
		)
	);

	State.allFocusableElements = State.allFocusableElements.filter((element) => {
		if (getComputedStyle(element).display === 'none') {
			return false;
		}

		if (getComputedStyle(element).visibility === 'hidden') {
			return false;
		}

		if (element.classList.contains('js-navigation-toggle')) {
			return true;
		} else {
			const parent = element.closest(
				'.m-target-group-selector-mobile:not(.m-target-group-selector-mobile--hidden), .o-navigation__mobile-navigation-list-container--current, .m-navigation-logo-container, .m-navigation-actions__item, .js-logout-link'
			);
			if (parent) {
				return getComputedStyle(parent).display !== 'none';
			}
		}

		return false;
	});

	return true;
}

/**
 * Focuses mobile navigation list element.
 *
 * @param {boolean} [focusPreviouslyClickedElement] - Focus previously clicked element.
 * @param {boolean} [skipUpdateNavigationFocusableElement=false] - Do not update focuseable elements.
 */
function focusMobileNavigationListElement(
	focusPreviouslyClickedElement,
	skipUpdateNavigationFocusableElement = false
) {
	if (!skipUpdateNavigationFocusableElement) {
		updateNavigationFocusableElements();
	}

	if (focusPreviouslyClickedElement && State.previousClickedElement) {
		State.previousClickedElement.focus();
		return;
	}

	const currentNavList = document.querySelector(
		'.o-navigation__mobile--onscreen .o-navigation__mobile-navigation-list-container--current'
	);
	if (currentNavList) {
		const firstElement = currentNavList.querySelector(
			'button, [href], [tabindex]:not([tabindex="-1"])'
		);
		if (firstElement) {
			firstElement.focus();
		}
	}
}

/**
 * Open mobile naviation.
 */
function openMobileNavigation() {
	const mobileNavigationContainer = document.querySelector(
		'.o-navigation__mobile'
	);

	if (mobileNavigationContainer) {
		const pageOverlay = document.createElement('div');
		pageOverlay.setAttribute('title', 'Sulje navigaatio');
		pageOverlay.classList.add('a-overlay-mask', 'a-overlay-mask--only-mobile');
		document.querySelector('.o-header .o-navigation').appendChild(pageOverlay);
		pageOverlay.addEventListener('click', (e) => {
			closeMobileNavigation();
		});
		State.menuTransitioning = true;
		requestAnimationFrame(() => {
			pageOverlay.classList.add('a-overlay-mask--active');
		});

		document.body.classList.add(
			'u-prevent-scroll',
			'u-prevent-scroll--until-widescreen'
		);
		renderMobileNavigation();
		mobileNavigationContainer.setAttribute('aria-hidden', false);

		mobileNavigationContainer.classList.remove(
			'o-navigation__mobile--offscreen'
		);
		requestAnimationFrame(() => {
			// Firefox workaraound. Single requestAnimationFrame should be enough to trigger CSS transition,
			// but for some reason that's not the case with Firefox.
			requestAnimationFrame(() => {
				mobileNavigationContainer.classList.add(
					'o-navigation__mobile--onscreen'
				);
			});
		});

		const timingMs = getTransitionDuration(
			mobileNavigationContainer,
			'transform'
		);
		setTimeout(() => {
			State.menuTransitioning = false;

			// TODO: Move a11y focus to close button after new timeout
		}, timingMs);

		NavigationListeners.addMobileNavigationListeners();
		setTimeout(
			updateNavigationFocusableElements,
			getTransitionDuration(mobileNavigationContainer, 'transform')
		);
		history.pushState(null, '');
	}
}

/**
 * Close mobile navigation.
 */
function closeMobileNavigation() {
	const pageOverlay = document.querySelectorAll('.a-overlay-mask');
	const mobileNavigationContainer = document.querySelector(
		'.o-navigation__mobile'
	);
	document.body.classList.remove(
		'u-prevent-scroll',
		'u-prevent-scroll--until-widescreen'
	);
	if (mobileNavigationContainer) {
		State.menuTransitioning = true;
		mobileNavigationContainer.setAttribute('aria-hidden', true);
		mobileNavigationContainer.classList.remove(
			'o-navigation__mobile--onscreen'
		);

		const timingMs = getTransitionDuration(
			mobileNavigationContainer,
			'transform'
		);

		setTimeout(() => {
			mobileNavigationContainer.classList.add(
				'o-navigation__mobile--offscreen'
			);
			State.menuTransitioning = false;
		}, timingMs);
	}
	if (pageOverlay) {
		for (const overlay of pageOverlay) {
			overlay.classList.remove('a-overlay-mask--active');
			const timingMs = getTransitionDuration(overlay, 'opacity');
			setTimeout(() => {
				for (const overlay of pageOverlay) {
					overlay.remove();
				}
			}, timingMs);
		}
	}
	NavigationListeners.removeMobileNavigationListeners();
	toggleMobileNavigationMenuButtonsStates(true);
}

/**
 * Toggles mobile navigation menu buttons states.
 *
 * @param {boolean} [close] - If true sets button states to closed, otherwise sets button states to open.
 * @param {Element} [elements] - Alternatively you can pass button element manually. By default uses .js-navigation-toggle selector to find relevant buttons.
 */
function toggleMobileNavigationMenuButtonsStates(close, elements) {
	const navButtons =
		elements || document.querySelectorAll('.js-navigation-toggle');
	for (const navButton of navButtons) {
		if (close) {
			navButton.classList.remove('a-toggle--active');
			navButton.setAttribute('aria-expanded', 'false');
			navButton.title = navButton.dataset.openText;
		} else {
			navButton.classList.add('a-toggle--active');
			navButton.setAttribute('aria-expanded', 'true');
			navButton.title = navButton.dataset.closeText;
		}
	}
}

function toggleCollapsibleSection(element) {
	if(!element) {
		return false;
	}
	const parent = element.closest('div');
	const section = parent.querySelector('.m-navigation-links--collapsible');
	const closed = section.classList.contains('m-navigation-links--collapsed');

	const title = parent.querySelector('.a-navigation-title');

	if (closed) {
		title.classList.add('a-navigation-title--open');
		section.classList.remove('m-navigation-links--collapsed');
		section.setAttribute('aria-expanded', 'true');
	} else {
		title.classList.remove('a-navigation-title--open');
		section.classList.add('m-navigation-links--collapsed');
		section.setAttribute('aria-expanded', 'false');
	}
}

export default {
	restoreNavigationState,
	readNavigationState,
	deleteNavigationState,
	debounceThrottle,
	selectNavigation,
	toggleDropdown,
	renderMobileNavigation,
	renderMobileNavigationLevel,
	openMobileNavigation,
	openDropdownNavigation,
	closeAllDropdownNavigations,
	toggleMobileNavigationMenuButtonsStates,
	closeMobileNavigation,
	goBackMobileNavigationLevel,
	saveNavigationState,
	focusMobileNavigationListElement,
	updateNavigationFocusableElements,
	updateGlobals,
	getTransitionDuration,
	toggleCollapsibleSection
};
